"use client";

import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import { DefaultPageLayout } from '../ui/layouts/DefaultPageLayout';
import { LoadingSpinner } from '../components/ui/LoadingSpinner';
import { useAuthStore } from '../store/authStore';
import { useNotificationStore } from '../store/notificationStore';
import { is3DModelUrl } from '../utils/media';
import {
  getCollectionDashboard,
  updateCollectionSettings,
  updateCollectionProfile,
  uploadCollectionLogo,
  getCustomTags,
  createCustomTag,
  updateCustomTag,
  deleteCustomTag,
  bulkUpdateTags,
  exportCustomTags,
  getCollectionOverview,
  exportCollectionMints,
  type CollectionDashboard,
  type CollectionSettings,
  type CollectionOverview,
  type CustomTag,
} from '../api/creator';

/**
 * Parse a CSV line handling quoted fields (RFC 4180 compliant)
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  let i = 0;

  while (i < line.length) {
    const char = line[i];

    if (inQuotes) {
      if (char === '"') {
        if (i + 1 < line.length && line[i + 1] === '"') {
          current += '"';
          i += 2;
          continue;
        }
        inQuotes = false;
      } else {
        current += char;
      }
    } else {
      if (char === '"') {
        inQuotes = true;
      } else if (char === ',') {
        result.push(current.trim());
        current = '';
      } else {
        current += char;
      }
    }
    i++;
  }

  result.push(current.trim());
  return result;
}

/**
 * Parse entire CSV text into headers and rows
 */
function parseCSV(text: string): { headers: string[]; rows: string[][] } {
  const lines = text.split('\n').filter(line => line.trim());
  if (lines.length === 0) {
    return { headers: [], rows: [] };
  }

  const headers = parseCSVLine(lines[0]);
  const rows = lines.slice(1).map(line => parseCSVLine(line));

  return { headers, rows };
}

import {
  FeatherLock,
  FeatherArrowLeft,
  FeatherSettings,
  FeatherTag,
  FeatherFileText,
  FeatherTrash2,
  FeatherExternalLink,
  FeatherUpload,
  FeatherDownload,
  FeatherAward,
  FeatherCamera,
  FeatherRefreshCw,
  FeatherChevronDown,
} from '@subframe/core';
import { ContestManager } from '../components/sweepstakes/ContestManager';
import { MutationsTab } from '../components/mutations/MutationsTab';

type TabId = 'overview' | 'settings' | 'tags' | 'contest' | 'mutations';

interface Tab {
  id: TabId;
  label: string;
  icon: React.ReactNode;
}

const TABS: Tab[] = [
  { id: 'overview', label: 'Overview', icon: <FeatherFileText className="h-4 w-4" /> },
  { id: 'tags', label: 'Custom Tags', icon: <FeatherTag className="h-4 w-4" /> },
  { id: 'mutations', label: 'Mutations', icon: <FeatherRefreshCw className="h-4 w-4" /> },
  { id: 'contest', label: 'Sweep Contest', icon: <FeatherAward className="h-4 w-4" /> },
  { id: 'settings', label: 'Settings', icon: <FeatherSettings className="h-4 w-4" /> },
];

export const CollectionManagePage: React.FC = () => {
  const { slug: projectSlugParam, collectionSlug } = useParams<{ slug?: string; collectionSlug: string }>();
  const projectSlug = projectSlugParam ?? null;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { isAuthenticated } = useAuthStore();
  const { success: showSuccess, error: showError } = useNotificationStore();

  const validTabs: TabId[] = ['overview', 'settings', 'tags', 'contest', 'mutations'];
  const tabFromUrl = searchParams.get('tab') as TabId | null;

  // State
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [dashboard, setDashboard] = useState<CollectionDashboard | null>(null);
  const [activeTab, setActiveTab] = useState<TabId>(
    tabFromUrl && validTabs.includes(tabFromUrl) ? tabFromUrl : 'overview'
  );
  const [error, setError] = useState<string | null>(null);

  // Overview data state
  const [overview, setOverview] = useState<CollectionOverview | null>(null);
  const [overviewLoading, setOverviewLoading] = useState(false);

  // Settings form state
  const [settingsForm, setSettingsForm] = useState<CollectionSettings>({
    rarity_methodology: null,
    richlist_point_strategy: null,
    preferred_marketplace: null,
  });

  // Description form state
  const [descriptionForm, setDescriptionForm] = useState('');
  const [savingDescription, setSavingDescription] = useState(false);

  // Custom tags state
  const [customTags, setCustomTags] = useState<CustomTag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [newTagDisplayName, setNewTagDisplayName] = useState('');
  const [newTagType, setNewTagType] = useState<'text' | 'number' | 'url' | 'boolean'>('text');
  const [newTagDescription, setNewTagDescription] = useState('');
  const [addingTag, setAddingTag] = useState(false);
  // Bulk tag upload state
  const [bulkTagFile, setBulkTagFile] = useState<File | null>(null);
  const [bulkTagPreview, setBulkTagPreview] = useState<{
    headers: string[];
    rows: Array<Record<string, string>>;
    matchedTags: Array<{ header: string; tag: CustomTag | null }>;
    totalRows: number;
  }>({ headers: [], rows: [], matchedTags: [], totalRows: 0 });
  const [uploadingBulkTags, setUploadingBulkTags] = useState(false);

  // Logo upload state
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const logoInputRef = React.useRef<HTMLInputElement>(null);

  // Load dashboard data
  const loadDashboard = useCallback(async () => {
    if (!collectionSlug || !isAuthenticated) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const data = await getCollectionDashboard(projectSlug, collectionSlug);
      setDashboard(data);

      // Initialize settings form
      setSettingsForm({
        rarity_methodology: data.collection.rarity_methodology,
        richlist_point_strategy: data.collection.richlist_point_strategy,
        preferred_marketplace: data.collection.preferred_marketplace,
      });
      setDescriptionForm(data.collection.description ?? '');
    } catch (err) {
      console.error('Error loading collection dashboard:', err);
      setError('Failed to load collection. You may not have access to manage this collection.');
    } finally {
      setLoading(false);
    }
  }, [projectSlug, collectionSlug, isAuthenticated]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  // Load overview data on mount (default tab)
  useEffect(() => {
    if (activeTab === 'overview' && dashboard && !overview) {
      loadOverview();
    }
  }, [activeTab, dashboard]);

  // Load custom tags when tab changes
  useEffect(() => {
    if (activeTab === 'tags' && dashboard) {
      loadCustomTags();
    }
  }, [activeTab, dashboard]);

  const loadOverview = async () => {
    if (!collectionSlug) return;

    setOverviewLoading(true);
    try {
      const data = await getCollectionOverview(projectSlug, collectionSlug);
      setOverview(data);
    } catch (err) {
      console.error('Error loading overview:', err);
    } finally {
      setOverviewLoading(false);
    }
  };

  const loadCustomTags = async () => {
    if (!collectionSlug) return;

    setTagsLoading(true);
    try {
      const tags = await getCustomTags(projectSlug, collectionSlug);
      setCustomTags(tags);
    } catch (err) {
      console.error('Error loading custom tags:', err);
      showError('Failed to load custom tags');
    } finally {
      setTagsLoading(false);
    }
  };

  // Save settings
  const handleSaveSettings = async () => {
    if (!collectionSlug || saving) return;

    setSaving(true);
    try {
      // Only send non-null values to avoid writing nulls to NOT NULL DB columns
      const payload: Record<string, any> = {};
      for (const [key, value] of Object.entries(settingsForm)) {
        if (value !== null && value !== undefined) {
          payload[key] = value;
        }
      }
      const result = await updateCollectionSettings(projectSlug, collectionSlug, payload);
      if (result.changes?.rarity_methodology) {
        showSuccess('Settings updated. Recalculating stats...');
      } else {
        showSuccess('Settings updated successfully');
      }
      await loadDashboard();
    } catch (err) {
      console.error('Error saving settings:', err);
      showError('Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  // Save description
  const handleSaveDescription = async () => {
    if (!collectionSlug || savingDescription) return;

    const trimmed = descriptionForm.trim();
    if (trimmed === (dashboard?.collection.description ?? '')) {
      return;
    }

    setSavingDescription(true);
    try {
      await updateCollectionProfile(projectSlug, collectionSlug, { description: trimmed });
      showSuccess('Description updated');
      await loadDashboard();
    } catch (err) {
      console.error('Error saving description:', err);
      showError(err instanceof Error ? err.message : 'Failed to update description');
    } finally {
      setSavingDescription(false);
    }
  };

  // Upload collection logo
  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !collectionSlug || uploadingLogo) return;

    // Client-side validation
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      showError('Invalid file type. Allowed: JPEG, PNG, WebP, GIF');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      showError('File too large. Maximum size is 5MB');
      return;
    }

    setUploadingLogo(true);
    try {
      const result = await uploadCollectionLogo(projectSlug, collectionSlug, file);
      showSuccess('Collection logo updated');
      // Update dashboard state with new image
      if (dashboard) {
        setDashboard({
          ...dashboard,
          collection: { ...dashboard.collection, image_url: result.image_url },
        });
      }
    } catch (err) {
      console.error('Error uploading logo:', err);
      showError(err instanceof Error ? err.message : 'Failed to upload logo');
    } finally {
      setUploadingLogo(false);
      // Reset input so same file can be re-selected
      if (logoInputRef.current) logoInputRef.current.value = '';
    }
  };

  // Create custom tag
  const handleCreateTag = async () => {
    if (!collectionSlug || !newTagName || !newTagDisplayName || addingTag) return;

    setAddingTag(true);
    try {
      await createCustomTag(projectSlug, collectionSlug, {
        name: newTagName,
        display_name: newTagDisplayName,
        tag_type: newTagType,
        description: newTagDescription || undefined,
      });
      showSuccess('Custom tag created');
      setNewTagName('');
      setNewTagDisplayName('');
      setNewTagType('text');
      setNewTagDescription('');
      await loadCustomTags();
    } catch (err) {
      console.error('Error creating custom tag:', err);
      showError(err instanceof Error ? err.message : 'Failed to create custom tag');
    } finally {
      setAddingTag(false);
    }
  };

  // Delete custom tag
  const handleDeleteTag = async (tagId: string, tagName: string) => {
    if (!collectionSlug) return;

    if (!window.confirm(`Are you sure you want to delete the tag "${tagName}"? This will remove all values assigned to NFTs.`)) {
      return;
    }

    try {
      await deleteCustomTag(projectSlug, collectionSlug, tagId);
      showSuccess('Custom tag deleted');
      await loadCustomTags();
    } catch (err) {
      console.error('Error deleting custom tag:', err);
      showError('Failed to delete custom tag');
    }
  };

  // Handle bulk tag CSV file selection
  const handleBulkTagFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setBulkTagFile(null);
      setBulkTagPreview({ headers: [], rows: [], matchedTags: [], totalRows: 0 });
      return;
    }

    setBulkTagFile(file);

    const reader = new FileReader();
    reader.onload = (event) => {
      const text = event.target?.result as string;
      const { headers, rows: allRows } = parseCSV(text);

      if (headers.length === 0 || allRows.length === 0) {
        showError('CSV must have a header row and at least one data row');
        setBulkTagPreview({ headers: [], rows: [], matchedTags: [], totalRows: 0 });
        return;
      }

      const tokenIdColIndex = headers.findIndex(h =>
        h.toLowerCase() === 'nft_token_id' || h.toLowerCase() === 'token_id'
      );

      if (tokenIdColIndex === -1) {
        showError('CSV must have a column named "nft_token_id" or "token_id"');
        setBulkTagPreview({ headers: [], rows: [], matchedTags: [], totalRows: 0 });
        return;
      }

      const matchedTags: Array<{ header: string; tag: CustomTag | null }> = headers.map((header, idx) => {
        if (idx === tokenIdColIndex) {
          return { header, tag: null };
        }
        const matchedTag = customTags.find(t =>
          t.name.toLowerCase() === header.toLowerCase()
        );
        return { header, tag: matchedTag || null };
      });

      const rows: Array<Record<string, string>> = [];
      for (const rowData of allRows.slice(0, 10)) {
        const row: Record<string, string> = {};
        headers.forEach((header, idx) => {
          row[header] = rowData[idx] || '';
        });
        if (row[headers[tokenIdColIndex]]) {
          rows.push(row);
        }
      }

      setBulkTagPreview({ headers, rows, matchedTags, totalRows: allRows.length });
    };
    reader.readAsText(file);
  };

  // Handle bulk tag upload
  const handleBulkTagUpload = async () => {
    if (!collectionSlug || !bulkTagFile || uploadingBulkTags) return;

    const matchedTagsWithValues = bulkTagPreview.matchedTags.filter(mt => mt.tag !== null);
    if (matchedTagsWithValues.length === 0) {
      showError('No valid tag columns found. Column headers must match tag names exactly.');
      return;
    }

    setUploadingBulkTags(true);
    try {
      const text = await bulkTagFile.text();
      const { headers, rows: allRows } = parseCSV(text);

      const tokenIdColIndex = headers.findIndex(h =>
        h.toLowerCase() === 'nft_token_id' || h.toLowerCase() === 'token_id'
      );

      let totalUpdated = 0;
      let totalErrors = 0;

      for (const { header, tag } of bulkTagPreview.matchedTags) {
        if (!tag) continue;

        const colIndex = headers.indexOf(header);
        if (colIndex === -1) continue;

        const updates: Array<{ nft_token_id: string; value: string }> = [];
        for (const rowData of allRows) {
          const nftTokenId = rowData[tokenIdColIndex];
          const value = rowData[colIndex];
          if (nftTokenId && value) {
            updates.push({ nft_token_id: nftTokenId, value });
          }
        }

        if (updates.length > 0) {
          try {
            const result = await bulkUpdateTags(projectSlug, collectionSlug, tag.id, updates);
            totalUpdated += result.updated;
            totalErrors += result.not_found?.length || 0;
          } catch {
            totalErrors += updates.length;
          }
        }
      }

      showSuccess(`Updated ${totalUpdated} tag values across ${matchedTagsWithValues.length} tags. ${totalErrors} errors.`);

      setBulkTagFile(null);
      setBulkTagPreview({ headers: [], rows: [], matchedTags: [], totalRows: 0 });
      const fileInput = document.getElementById('bulk-tag-file-input') as HTMLInputElement;
      if (fileInput) fileInput.value = '';
    } catch (err) {
      console.error('Error uploading bulk tags:', err);
      showError('Failed to upload bulk tags');
    } finally {
      setUploadingBulkTags(false);
    }
  };

  // Export custom tags as CSV
  const [exportingTags, setExportingTags] = useState(false);
  const handleExportTags = async () => {
    if (!collectionSlug || exportingTags) return;

    setExportingTags(true);
    try {
      const data = await exportCustomTags(projectSlug, collectionSlug);

      if (data.nfts.length === 0) {
        showError('No NFTs have custom tag values set yet');
        return;
      }

      const tagNames = data.tags.map(t => t.name);
      const headers = ['nft_token_id', ...tagNames];

      const escapeCSV = (val: string) => {
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const rows = data.nfts.map(nft => {
        const values = [nft.nft_token_id, ...tagNames.map(name => nft.tags[name] || '')];
        return values.map(escapeCSV).join(',');
      });

      const csv = [headers.join(','), ...rows].join('\n');

      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collectionSlug}-custom-tags.csv`;
      a.click();
      URL.revokeObjectURL(url);

      showSuccess(`Exported ${data.nfts.length} NFTs with custom tags`);
    } catch (err) {
      console.error('Error exporting tags:', err);
      showError(err instanceof Error ? err.message : 'Failed to export custom tags');
    } finally {
      setExportingTags(false);
    }
  };

  // Export mints as CSV
  const [exportingMints, setExportingMints] = useState(false);
  const [mintExportOpen, setMintExportOpen] = useState(false);
  const mintExportRef = React.useRef<HTMLDivElement>(null);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (mintExportRef.current && !mintExportRef.current.contains(e.target as Node)) {
        setMintExportOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleExportMints = async (days: 1 | 3 | 7 | 30) => {
    if (!collectionSlug || exportingMints) return;

    setExportingMints(true);
    setMintExportOpen(false);
    try {
      const data = await exportCollectionMints(projectSlug, collectionSlug, days);

      if (data.mints.length === 0) {
        showError(`No mints found in the last ${days} day${days > 1 ? 's' : ''}`);
        return;
      }

      const escapeCSV = (val: string) => {
        if (val.includes(',') || val.includes('"') || val.includes('\n')) {
          return `"${val.replace(/"/g, '""')}"`;
        }
        return val;
      };

      const headers = ['nft_token_id', 'nft_name', 'minted_to', 'minted_at', 'tx_hash'];
      const rows = data.mints.map(mint => {
        return [
          mint.nft_token_id,
          mint.nft_name || '',
          mint.to_address,
          mint.occurred_at,
          mint.hash,
        ].map(escapeCSV).join(',');
      });

      const csv = [headers.join(','), ...rows].join('\n');
      const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${collectionSlug}-mints-${days}d.csv`;
      a.click();
      URL.revokeObjectURL(url);

      showSuccess(`Exported ${data.mints.length} mints from the last ${days} day${days > 1 ? 's' : ''}`);
    } catch (err) {
      console.error('Error exporting mints:', err);
      showError(err instanceof Error ? err.message : 'Failed to export mints');
    } finally {
      setExportingMints(false);
    }
  };

  // Format date
  const formatDate = (dateStr: string): string => {
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // Format price
  const formatPrice = (price: number | null | undefined): string => {
    if (price === null || price === undefined) return '-';
    const num = typeof price === 'string' ? parseFloat(price) : price;
    if (isNaN(num)) return '-';
    if (num >= 1000000) return `${(num / 1000000).toFixed(2)}M`;
    if (num >= 1000) return `${(num / 1000).toFixed(1)}K`;
    return num.toFixed(2);
  };

  // Format time ago
  const timeAgo = (dateStr: string): string => {
    const seconds = Math.floor((Date.now() - new Date(dateStr).getTime()) / 1000);
    if (seconds < 60) return 'just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}d ago`;
    return formatDate(dateStr);
  };

  // Format address
  const formatAddress = (addr: string): string =>
    addr ? `${addr.slice(0, 8)}...${addr.slice(-6)}` : '';

  // Not authenticated
  if (!isAuthenticated) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16 gap-4">
          <FeatherLock className="h-12 w-12 text-neutral-400" />
          <h2 className="text-heading-3 font-heading-3 text-default-font">Sign In Required</h2>
          <p className="text-body font-body text-subtext-color text-center max-w-md">
            Please sign in to manage this collection.
          </p>
        </div>
      </DefaultPageLayout>
    );
  }

  // Loading state
  if (loading) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-body font-body text-subtext-color">Loading collection...</p>
        </div>
      </DefaultPageLayout>
    );
  }

  // Error state
  if (error || !dashboard) {
    return (
      <DefaultPageLayout>
        <div className="flex w-full flex-col items-center justify-center py-16 gap-4">
          <FeatherLock className="h-12 w-12 text-neutral-400" />
          <p className="text-body font-body text-error-600">{error || 'Collection not found'}</p>
          <button
            onClick={() => navigate(projectSlug ? `/creator/projects/${projectSlug}` : '/creator')}
            className="flex items-center gap-2 text-brand-600 hover:text-brand-700"
          >
            <FeatherArrowLeft className="h-4 w-4" />
            {projectSlug ? 'Back to Project' : 'Back to Dashboard'}
          </button>
        </div>
      </DefaultPageLayout>
    );
  }

  return (
    <DefaultPageLayout>
      <div className="flex w-full flex-col items-start gap-6">
        {/* Header */}
        <div className="flex items-center gap-4 w-full">
          <button
            onClick={() => navigate(projectSlug ? `/creator/projects/${projectSlug}` : '/creator')}
            className="p-2 rounded-md hover:bg-default-background transition-colors"
          >
            <FeatherArrowLeft className="h-5 w-5 text-neutral-500" />
          </button>
          <div className="flex items-center gap-4 flex-1">
            {dashboard.collection.image_url && (
              <img
                src={dashboard.collection.image_url}
                alt={dashboard.collection.name}
                className="w-12 h-12 rounded-lg object-cover"
              />
            )}
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-heading-2 font-heading-2 text-default-font">
                  {dashboard.collection.name}
                </h1>
              </div>
              <div className="flex items-center gap-2 text-caption font-caption text-subtext-color">
                <span>
                  {dashboard.collection.creator_address.slice(0, 8)}...
                  {dashboard.collection.creator_address.slice(-6)}
                </span>
                {dashboard.project && (
                  <span className="px-2 py-0.5 rounded-full bg-brand-100 text-brand-700 text-xs font-medium">
                    {dashboard.project.name}
                  </span>
                )}
              </div>
            </div>
          </div>
          <a
            href={`/collections/${dashboard.collection.slug || dashboard.collection.id}`}
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center gap-2 px-3 py-2 rounded-md border border-neutral-border bg-default-background text-subtext-color hover:text-default-font transition-colors"
          >
            <FeatherExternalLink className="h-4 w-4" />
            View Collection
          </a>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 border-b border-neutral-border w-full overflow-x-auto">
          {TABS.filter((tab) => tab.id !== 'mutations' || dashboard?.has_mutable_nfts).map((tab) => (
            <button
              key={tab.id}
              onClick={() => {
                setActiveTab(tab.id);
                setSearchParams(tab.id === 'overview' ? {} : { tab: tab.id }, { replace: true });
              }}
              className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-colors whitespace-nowrap ${
                activeTab === tab.id
                  ? 'border-brand-600 text-brand-600'
                  : 'border-transparent text-subtext-color hover:text-default-font'
              }`}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>

        {/* Tab Content */}
        <div className="w-full">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              {/* Collection Info + Stats side by side on larger screens */}
              <div className="grid grid-cols-2 gap-6 mobile:grid-cols-1">
                {/* Collection Info */}
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">Collection Info</h3>
                  <div className="space-y-3">
                    <div>
                      <p className="text-caption font-caption text-subtext-color">Name</p>
                      <p className="text-body font-body text-default-font">{dashboard.collection.name}</p>
                    </div>
                    {dashboard.collection.description && (
                      <div>
                        <p className="text-caption font-caption text-subtext-color">Description</p>
                        <p className="text-body font-body text-default-font">{dashboard.collection.description}</p>
                      </div>
                    )}
                    {dashboard.collection.website_url && (
                      <div>
                        <p className="text-caption font-caption text-subtext-color">Website</p>
                        <a
                          href={dashboard.collection.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-body font-body text-brand-700 hover:text-brand-600 underline flex items-center gap-1"
                        >
                          {dashboard.collection.website_url}
                          <FeatherExternalLink className="h-3 w-3" />
                        </a>
                      </div>
                    )}
                  </div>
                </div>

                {/* Stats Grid */}
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">Statistics</h3>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-caption font-caption text-subtext-color">Floor Price</p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {formatPrice(dashboard.stats.floor_price)} XRP
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">Total Volume</p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {formatPrice(dashboard.stats.total_volume)} XRP
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">24h Volume</p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {formatPrice(dashboard.stats.volume_24h)} XRP
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">Total Supply</p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {dashboard.stats.total_supply}
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">Listed</p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {dashboard.stats.listed_count}
                      </p>
                    </div>
                    <div>
                      <p className="text-caption font-caption text-subtext-color">Unique Owners</p>
                      <p className="text-heading-3 font-heading-3 text-default-font">
                        {dashboard.stats.unique_owners}
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {overviewLoading ? (
                <div className="flex justify-center py-8">
                  <LoadingSpinner size="md" />
                </div>
              ) : overview && (
                <>
                  {/* Recent Sales */}
                  <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                    <h3 className="text-body-bold font-body-bold text-default-font mb-4">Recent Sales</h3>
                    {overview.recentSales.length === 0 ? (
                      <p className="text-body font-body text-subtext-color text-center py-4">
                        No recent sales recorded.
                      </p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-neutral-border">
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Item</th>
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Price</th>
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Time</th>
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">From</th>
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">To</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overview.recentSales.map((sale) => (
                              <tr key={sale.hash} className="border-t border-neutral-border">
                                <td className="h-12 px-3 min-w-[180px]">
                                  <Link
                                    to={`/nfts/${sale.nft_token_id}`}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                  >
                                    {sale.nft_thumbnail_url && (
                                      <img src={sale.nft_thumbnail_url} alt="" className="w-10 h-10 rounded object-cover" />
                                    )}
                                    <span className="text-body font-body text-default-font">
                                      {sale.nft_name || `NFT #${sale.nft_token_id.slice(0, 8)}...`}
                                    </span>
                                  </Link>
                                </td>
                                <td className="h-12 px-3 whitespace-nowrap text-body font-body text-default-font">
                                  {formatPrice(sale.amount)} XRP
                                </td>
                                <td className="h-12 px-3 whitespace-nowrap text-body font-body text-subtext-color">
                                  {timeAgo(sale.occurred_at)}
                                </td>
                                <td className="h-12 px-3">
                                  <Link
                                    to={`/wallets/${sale.from_address}`}
                                    className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                                  >
                                    {formatAddress(sale.from_address)}
                                  </Link>
                                </td>
                                <td className="h-12 px-3">
                                  <Link
                                    to={`/wallets/${sale.to_address}`}
                                    className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                                  >
                                    {formatAddress(sale.to_address)}
                                  </Link>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {/* Recent Mints */}
                  {overview.recentMints.length > 0 && (
                    <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                      <div className="flex items-center justify-between mb-4">
                        <h3 className="text-body-bold font-body-bold text-default-font">Recent Mints</h3>
                        <div ref={mintExportRef} className="relative">
                          <button
                            onClick={() => setMintExportOpen(!mintExportOpen)}
                            disabled={exportingMints}
                            className="flex items-center gap-1.5 px-3 py-1.5 rounded-md border border-neutral-border bg-default-background text-body font-body text-default-font hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                          >
                            <FeatherDownload className="h-3.5 w-3.5" />
                            {exportingMints ? 'Exporting...' : 'Download CSV'}
                            <FeatherChevronDown className="h-3.5 w-3.5" />
                          </button>
                          {mintExportOpen && (
                            <div className="absolute right-0 top-full mt-1 w-40 rounded-md border border-neutral-border bg-default-background shadow-lg z-10">
                              {([1, 3, 7, 30] as const).map((days) => (
                                <button
                                  key={days}
                                  onClick={() => handleExportMints(days)}
                                  className="w-full text-left px-4 py-2 text-body font-body text-default-font hover:bg-neutral-50 first:rounded-t-md last:rounded-b-md transition-colors"
                                >
                                  Last {days} day{days > 1 ? 's' : ''}
                                </button>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full">
                          <thead>
                            <tr className="border-b border-neutral-border">
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Item</th>
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Minted To</th>
                              <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Time</th>
                            </tr>
                          </thead>
                          <tbody>
                            {overview.recentMints.map((mint) => (
                              <tr key={mint.hash} className="border-t border-neutral-border">
                                <td className="h-12 px-3 min-w-[180px]">
                                  <Link
                                    to={`/nfts/${mint.nft_token_id}`}
                                    className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                  >
                                    {mint.nft_thumbnail_url && (
                                      <img src={mint.nft_thumbnail_url} alt="" className="w-10 h-10 rounded object-cover" />
                                    )}
                                    <span className="text-body font-body text-default-font">
                                      {mint.nft_name || `NFT #${mint.nft_token_id.slice(0, 8)}...`}
                                    </span>
                                  </Link>
                                </td>
                                <td className="h-12 px-3">
                                  <Link
                                    to={`/wallets/${mint.to_address}`}
                                    className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                                  >
                                    {formatAddress(mint.to_address)}
                                  </Link>
                                </td>
                                <td className="h-12 px-3 whitespace-nowrap text-body font-body text-subtext-color">
                                  {timeAgo(mint.occurred_at)}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}

                  {/* Top Holders + Cheapest Listings side by side */}
                  <div className="grid grid-cols-2 gap-6 mobile:grid-cols-1">
                    {/* Top Holders */}
                    <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                      <h3 className="text-body-bold font-body-bold text-default-font mb-4">
                        Top Holders
                        {dashboard.stats.unique_owners > 0 && (
                          <span className="text-caption font-caption text-subtext-color ml-2">
                            ({dashboard.stats.unique_owners} unique)
                          </span>
                        )}
                      </h3>
                      {overview.holderDistribution.length === 0 ? (
                        <p className="text-body font-body text-subtext-color text-center py-4">
                          No holder data available.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-neutral-border">
                                <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">#</th>
                                <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Address</th>
                                <th className="h-8 px-3 text-right whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">NFTs</th>
                                <th className="h-8 px-3 text-right whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">%</th>
                              </tr>
                            </thead>
                            <tbody>
                              {overview.holderDistribution.map((holder, idx) => (
                                <tr key={holder.owner_address} className="border-t border-neutral-border">
                                  <td className="h-12 px-3 text-body font-body text-subtext-color">{idx + 1}</td>
                                  <td className="h-12 px-3">
                                    <Link
                                      to={`/wallets/${holder.owner_address}`}
                                      className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                                    >
                                      {formatAddress(holder.owner_address)}
                                    </Link>
                                  </td>
                                  <td className="h-12 px-3 text-right text-body font-body text-default-font">{holder.nft_count}</td>
                                  <td className="h-12 px-3 text-right text-body font-body text-default-font">
                                    {dashboard.stats.total_supply > 0
                                      ? ((holder.nft_count / dashboard.stats.total_supply) * 100).toFixed(1)
                                      : '0'}%
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>

                    {/* Cheapest Listings */}
                    <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                      <h3 className="text-body-bold font-body-bold text-default-font mb-4">Cheapest Listings</h3>
                      {overview.cheapestListings.length === 0 ? (
                        <p className="text-body font-body text-subtext-color text-center py-4">
                          No NFTs currently listed for sale.
                        </p>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead>
                              <tr className="border-b border-neutral-border">
                                <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Item</th>
                                <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Price</th>
                                <th className="h-8 px-3 text-left whitespace-nowrap text-caption-bold font-caption-bold text-subtext-color">Owner</th>
                              </tr>
                            </thead>
                            <tbody>
                              {overview.cheapestListings.map((listing) => (
                                <tr key={listing.token_id} className="border-t border-neutral-border">
                                  <td className="h-12 px-3 min-w-[140px]">
                                    <Link
                                      to={`/nfts/${listing.token_id}`}
                                      className="flex items-center gap-2 hover:opacity-80 transition-opacity"
                                    >
                                      {listing.thumbnail_url && (
                                        is3DModelUrl(listing.thumbnail_url) ? (
                                          <div className="w-10 h-10 rounded flex items-center justify-center bg-neutral-100">
                                            <span style={{ fontSize: '16px' }}>🧊</span>
                                          </div>
                                        ) : (
                                          <img src={listing.thumbnail_url} alt="" className="w-10 h-10 rounded object-cover" />
                                        )
                                      )}
                                      <span className="text-body font-body text-default-font">
                                        {listing.name || `NFT #${listing.token_id.slice(0, 8)}...`}
                                      </span>
                                    </Link>
                                  </td>
                                  <td className="h-12 px-3 whitespace-nowrap text-body font-body text-default-font">
                                    {formatPrice(listing.min_sell_price)} XRP
                                  </td>
                                  <td className="h-12 px-3">
                                    <Link
                                      to={`/wallets/${listing.owner_address}`}
                                      className="whitespace-nowrap text-body font-body text-brand-700 hover:text-brand-600 underline"
                                    >
                                      {formatAddress(listing.owner_address)}
                                    </Link>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              )}
            </div>
          )}

          {/* Settings Tab */}
          {activeTab === 'settings' && (
            <div className="max-w-2xl space-y-6">
              {/* Collection Logo */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Collection Logo</h3>
                <div className="flex items-center gap-6">
                  <div className="relative group">
                    <div className="w-24 h-24 rounded-lg overflow-hidden border-2 border-neutral-border bg-neutral-100 flex items-center justify-center">
                      {dashboard?.collection.image_url ? (
                        <img
                          src={dashboard.collection.image_url}
                          alt={dashboard.collection.name}
                          className="w-full h-full object-cover"
                        />
                      ) : (
                        <FeatherCamera className="h-8 w-8 text-neutral-400" />
                      )}
                    </div>
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="absolute inset-0 flex items-center justify-center bg-black/50 opacity-0 group-hover:opacity-100 rounded-lg transition-opacity cursor-pointer"
                    >
                      {uploadingLogo ? (
                        <LoadingSpinner size="sm" />
                      ) : (
                        <FeatherCamera className="h-6 w-6 text-white" />
                      )}
                    </button>
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/webp,image/gif"
                      onChange={handleLogoUpload}
                      className="hidden"
                    />
                  </div>
                  <div className="flex-1">
                    <p className="text-body font-body text-default-font mb-1">
                      Update your collection's logo
                    </p>
                    <p className="text-caption font-caption text-subtext-color mb-3">
                      JPEG, PNG, WebP, or GIF. Max 5MB. Will be cropped to a square.
                    </p>
                    <button
                      onClick={() => logoInputRef.current?.click()}
                      disabled={uploadingLogo}
                      className="px-4 py-2 rounded-md border border-neutral-border bg-default-background text-default-font hover:bg-neutral-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploadingLogo ? 'Uploading...' : 'Choose Image'}
                    </button>
                  </div>
                </div>
              </div>

              {/* Collection Description */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Description</h3>
                <p className="text-caption font-caption text-subtext-color mb-3">
                  Tell collectors about your collection. Shown on the collection page.
                </p>
                <textarea
                  value={descriptionForm}
                  onChange={(e) => setDescriptionForm(e.target.value)}
                  maxLength={5000}
                  rows={6}
                  placeholder="Share the story, lore, or roadmap behind this collection..."
                  className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
                />
                <div className="flex items-center justify-between mt-2">
                  <span className="text-caption font-caption text-subtext-color">
                    {descriptionForm.length} / 5000
                  </span>
                  <button
                    onClick={handleSaveDescription}
                    disabled={
                      savingDescription ||
                      descriptionForm.trim() === (dashboard?.collection.description ?? '')
                    }
                    className="px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {savingDescription ? 'Saving...' : 'Save Description'}
                  </button>
                </div>
              </div>

              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Collection Settings</h3>
                <div className="space-y-4">
                  <div>
                    <label className="block text-caption font-caption text-subtext-color mb-1">
                      Rarity Methodology
                    </label>
                    <select
                      value={settingsForm.rarity_methodology || 'TRAIT_SCORE'}
                      onChange={(e) => setSettingsForm({ ...settingsForm, rarity_methodology: e.target.value })}
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="TRAIT_SCORE">Trait Score</option>
                      <option value="STATISTICAL">Statistical</option>
                      <option value="AVERAGE_PERCENTAGE">Average Percentage</option>
                      <option value="NONE">None (Disable Rarity)</option>
                    </select>
                    <p className="text-caption font-caption text-subtext-color mt-1">
                      {settingsForm.rarity_methodology === 'STATISTICAL'
                        ? 'Uses statistical z-scores to measure how unusual each trait is compared to the collection average. Better handles skewed trait distributions than simpler methods.'
                        : settingsForm.rarity_methodology === 'AVERAGE_PERCENTAGE'
                        ? 'Averages each NFT\'s trait percentages. Used by XRP Cafe. Naturally rewards NFTs with fewer traits.'
                        : settingsForm.rarity_methodology === 'NONE'
                        ? 'Disables rarity calculation entirely. Rarity scores and ranks will not be shown for this collection.'
                        : 'Industry standard method (used by rarity.tools). Sums the inverse of each trait\'s percentage — rarer traits contribute more to the score. Higher score = rarer NFT.'}
                    </p>
                  </div>
                  <div>
                    <label className="block text-caption font-caption text-subtext-color mb-1">
                      Preferred Marketplace
                    </label>
                    <select
                      value={settingsForm.preferred_marketplace || ''}
                      onChange={(e) => setSettingsForm({ ...settingsForm, preferred_marketplace: e.target.value || null })}
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="">xrp.deals (default)</option>
                      <option value="xrp.cafe">xrp.cafe</option>
                      <option value="bidds.com">bidds.com</option>
                      <option value="opmarket.ai">opmarket.ai</option>
                    </select>
                  </div>
                  <button
                    onClick={handleSaveSettings}
                    disabled={saving}
                    className="w-full px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {saving ? 'Saving...' : 'Save Settings'}
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Custom Tags Tab */}
          {activeTab === 'tags' && (
            <div className="space-y-6">
              {/* Create Tag */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Create Custom Tag</h3>
                <p className="text-caption font-caption text-subtext-color mb-4">
                  Custom tags allow you to add metadata to NFTs in your collection that isn't part of their on-chain traits.
                </p>
                <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1">
                  <div>
                    <label className="block text-caption font-caption text-subtext-color mb-1">
                      Tag Name (internal)
                    </label>
                    <input
                      type="text"
                      value={newTagName}
                      onChange={(e) => setNewTagName(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, '_'))}
                      placeholder="e.g., episode_number"
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-caption font-caption text-subtext-color mb-1">
                      Display Name
                    </label>
                    <input
                      type="text"
                      value={newTagDisplayName}
                      onChange={(e) => setNewTagDisplayName(e.target.value)}
                      placeholder="e.g., Episode Number"
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                  <div>
                    <label className="block text-caption font-caption text-subtext-color mb-1">
                      Tag Type
                    </label>
                    <select
                      value={newTagType}
                      onChange={(e) => setNewTagType(e.target.value as 'text' | 'number' | 'url' | 'boolean')}
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                    >
                      <option value="text">Text</option>
                      <option value="number">Number</option>
                      <option value="url">URL (clickable link)</option>
                      <option value="boolean">Boolean (Yes/No)</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-caption font-caption text-subtext-color mb-1">
                      Description (optional)
                    </label>
                    <input
                      type="text"
                      value={newTagDescription}
                      onChange={(e) => setNewTagDescription(e.target.value)}
                      placeholder="What this tag represents"
                      className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500"
                    />
                  </div>
                </div>
                <button
                  onClick={handleCreateTag}
                  disabled={addingTag || !newTagName || !newTagDisplayName}
                  className="mt-4 flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                >
                  {addingTag ? <LoadingSpinner size="sm" /> : <FeatherTag className="h-4 w-4" />}
                  Create Tag
                </button>
              </div>

              {/* Tags List */}
              <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                <h3 className="text-body-bold font-body-bold text-default-font mb-4">Custom Tags</h3>
                {tagsLoading ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner size="md" />
                  </div>
                ) : customTags.length === 0 ? (
                  <p className="text-body font-body text-subtext-color text-center py-8">
                    No custom tags defined yet. Create one above to get started.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {customTags.map((tag) => (
                      <div
                        key={tag.id}
                        className="flex items-center justify-between p-4 rounded-lg border border-neutral-border bg-default-background"
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <p className="text-body-bold font-body-bold text-default-font">
                              {tag.display_name}
                            </p>
                            <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-700">
                              {tag.tag_type}
                            </span>
                          </div>
                          <p className="text-caption font-caption text-subtext-color">
                            {tag.name}
                            {tag.description && ` - ${tag.description}`}
                          </p>
                        </div>
                        <button
                          onClick={() => handleDeleteTag(tag.id, tag.display_name)}
                          className="p-2 rounded-md text-red-500 hover:bg-red-50 transition-colors"
                        >
                          <FeatherTrash2 className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bulk Tag Upload */}
              {customTags.length > 0 && (
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">Bulk Update Tag Values</h3>
                  <p className="text-caption font-caption text-subtext-color mb-4">
                    Upload a CSV file to set tag values for multiple NFTs at once. Your CSV must use the following header format:
                  </p>
                  <p className="text-caption font-caption text-subtext-color mb-4">
                    <code className="px-1 py-0.5 rounded bg-neutral-100 text-neutral-700">
                      nft_token_id,{customTags.map(t => t.name).join(',')}
                    </code>
                  </p>
                  <p className="text-caption font-caption text-subtext-color mb-4">
                    You can include all tags or just the ones you want to update. Empty values will be skipped.
                  </p>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-caption font-caption text-subtext-color mb-1">
                        CSV File
                      </label>
                      <input
                        id="bulk-tag-file-input"
                        type="file"
                        accept=".csv,.txt"
                        onChange={handleBulkTagFileSelect}
                        className="w-full px-3 py-2 rounded-md border border-neutral-border bg-default-background text-default-font focus:outline-none focus:ring-2 focus:ring-brand-500 file:mr-4 file:py-1 file:px-3 file:rounded-md file:border-0 file:bg-brand-100 file:text-brand-700 file:cursor-pointer hover:file:bg-brand-200"
                      />
                    </div>

                    {/* Column Matching Status */}
                    {bulkTagPreview.matchedTags.length > 0 && (
                      <div>
                        <p className="text-caption font-caption text-subtext-color mb-2">
                          Column matching:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {bulkTagPreview.matchedTags.map(({ header, tag }, idx) => (
                            <span
                              key={idx}
                              className={`px-2 py-1 rounded-full text-xs font-medium ${
                                header.toLowerCase() === 'nft_token_id' || header.toLowerCase() === 'token_id'
                                  ? 'bg-blue-100 text-blue-700'
                                  : tag
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}
                            >
                              {header}
                              {tag ? ` → ${tag.display_name}` : header.toLowerCase() === 'nft_token_id' || header.toLowerCase() === 'token_id' ? ' (ID)' : ' (not matched)'}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Preview */}
                    {bulkTagPreview.rows.length > 0 && (
                      <div>
                        <p className="text-caption font-caption text-subtext-color mb-2">
                          Preview (first {bulkTagPreview.rows.length} of {bulkTagPreview.totalRows} rows):
                        </p>
                        <div className="overflow-x-auto rounded-md border border-neutral-border">
                          <table className="w-full text-sm">
                            <thead className="bg-default-background border-b border-neutral-border">
                              <tr>
                                {bulkTagPreview.headers.map((header, idx) => (
                                  <th key={idx} className="px-3 py-2 text-left text-caption font-caption text-subtext-color whitespace-nowrap">
                                    {header}
                                  </th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {bulkTagPreview.rows.map((row, idx) => (
                                <tr key={idx} className="border-t border-neutral-border">
                                  {bulkTagPreview.headers.map((header, colIdx) => (
                                    <td key={colIdx} className={`px-3 py-2 text-caption text-default-font ${colIdx === 0 ? 'font-mono' : ''}`}>
                                      {colIdx === 0 ? `${row[header]?.slice(0, 16)}...` : row[header] || '-'}
                                    </td>
                                  ))}
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    )}

                    {/* Upload Button */}
                    <button
                      onClick={handleBulkTagUpload}
                      disabled={uploadingBulkTags || !bulkTagFile || bulkTagPreview.matchedTags.filter(mt => mt.tag).length === 0}
                      className="flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    >
                      {uploadingBulkTags ? <LoadingSpinner size="sm" /> : <FeatherUpload className="h-4 w-4" />}
                      Upload & Update
                    </button>
                  </div>
                </div>
              )}

              {/* Export Tags */}
              {customTags.length > 0 && (
                <div className="p-6 rounded-lg border border-neutral-border bg-default-background">
                  <h3 className="text-body-bold font-body-bold text-default-font mb-4">Export Tag Values</h3>
                  <p className="text-caption font-caption text-subtext-color mb-4">
                    Download a CSV of all NFTs that have custom tag values set. The exported file uses the same
                    format as the import, so you can edit it and re-upload to update values.
                  </p>
                  <button
                    onClick={handleExportTags}
                    disabled={exportingTags}
                    className="flex items-center gap-2 px-4 py-2 rounded-md bg-brand-600 text-white hover:bg-brand-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    {exportingTags ? <LoadingSpinner size="sm" /> : <FeatherDownload className="h-4 w-4" />}
                    Export CSV
                  </button>
                </div>
              )}
            </div>
          )}
          {/* Mutations Tab */}
          {activeTab === 'mutations' && dashboard && collectionSlug && (
            <MutationsTab
              projectSlug={projectSlug}
              collectionSlug={collectionSlug}
              collectionName={dashboard.collection.name}
              showSuccess={showSuccess}
              showError={showError}
            />
          )}
          {/* Sweep Contest Tab */}
          {activeTab === 'contest' && dashboard && (
            <ContestManager collectionId={dashboard.collection.id} />
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};
